"use client";

import { useState, useEffect } from "react";
import { forwardRef } from "react";
import { Input, InputGroup, InputRightContent } from "./input";
import { EyeIcon, EyeOffIcon } from "~/components/icons";
import { cn } from "~/lib/utils";

export interface PasswordValidation {
  hasLowercase: boolean;
  hasUppercase: boolean;
  hasNumber: boolean;
  minLength: boolean;
}

export interface PasswordFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "size"> {
  label?: string;
  showValidation?: boolean;
  onValidationChange?: (isValid: boolean) => void;
  size?: "sm" | "md" | "lg";
  className?: string;
  invalid?: boolean;
}

const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  (
    {
      label,
      value,
      onChange,
      showValidation = false,
      onValidationChange,
      size = "md",
      className,
      invalid,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const password = (value as string) ?? "";

    const validation: PasswordValidation = {
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      minLength: password.length >= 8,
    };

    const isValid =
      validation.hasLowercase &&
      validation.hasUppercase &&
      validation.hasNumber &&
      validation.minLength;

    // Notify parent of validation state using useEffect to avoid render-time updates
    useEffect(() => {
      onValidationChange?.(isValid);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValid]); // onValidationChange is a callback prop, intentionally excluded

    return (
      <div className={cn("flex flex-col gap-2", className)}>
        {label && (
          <label className="text-sm font-semibold leading-5 text-[#1c2024]">
            {label}
          </label>
        )}
        <InputGroup>
          <Input
            ref={ref}
            type={showPassword ? "text" : "password"}
            value={value}
            onChange={onChange}
            size={size}
            invalid={invalid ?? (showValidation && password.length > 0 && !isValid)}
            {...props}
          />
          <InputRightContent>
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-[#60646c] hover:text-[#1c2024]"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOffIcon className="h-4 w-4" />
              ) : (
                <EyeIcon className="h-4 w-4" />
              )}
            </button>
          </InputRightContent>
        </InputGroup>
        {showValidation && password.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <ValidationItem
              isValid={validation.hasLowercase}
              label="At least one lowercase letter"
            />
            <ValidationItem
              isValid={validation.minLength}
              label="Minimum 8 characters"
            />
            <ValidationItem
              isValid={validation.hasUppercase}
              label="At least one uppercase letter"
            />
            <ValidationItem
              isValid={validation.hasNumber}
              label="At least one number"
            />
          </div>
        )}
      </div>
    );
  }
);

PasswordField.displayName = "PasswordField";

interface ValidationItemProps {
  isValid: boolean;
  label: string;
}

function ValidationItem({ isValid, label }: ValidationItemProps) {
  return (
    <div className="flex items-center gap-2">
      {isValid ? (
        <svg
          className="h-4 w-4 text-green-600"
          viewBox="0 0 16 16"
          fill="none"
        >
          <circle cx="8" cy="8" r="7" fill="currentColor" />
          <path
            d="M5 8L7 10L11 6"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg
          className="h-4 w-4 text-[#60646c]"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M8 7V11M8 5V5.01M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      <span
        className={cn(
          "text-sm leading-6",
          isValid ? "text-[#1c2024]" : "text-[#60646c]"
        )}
      >
        {label}
      </span>
    </div>
  );
}

export { PasswordField };

